import {
  AmountType,
  CalculationMethod,
  CommissionTierDrafts,
  CreateCommissionInput,
} from "../../../types/commission";
import { parseCommissionValue } from "../CommissionInput";
import { formatDateToString } from "../commissionFormHelpers";
import {
  draftsToWire,
  validateTierDrafts,
} from "../../../utils/commissionUtils";
import { DraftsToOverridesResult } from "../../../utils/serviceCommissionOverrides";

/** What the commission form holds when the user presses Save. */
export interface CommissionFormValues {
  staffId: string;
  /** Set when editing a saved record. */
  recordId?: string;
  calculationMethod: CalculationMethod;
  /** Omitted for tier templates, which have no date. */
  requireDate: boolean;
  effectiveStartDate: Date | null;
  serviceCommission: string;
  productCommission: string;
  numServicesCutoff: number | "";
  tierDrafts: CommissionTierDrafts;
  /** Service-specific mode: the default service rate is saved as 0%. */
  serviceSpecific: boolean;
  /** The per-service rates to send; null when the feature is off (the key is
   * then left out, so the backend keeps or carries forward saved rates). */
  overrides: DraftsToOverridesResult | null;
  /** A field already shows an inline validation error. */
  hasFieldErrors: boolean;
}

export type BuildCommissionResult =
  | { input: CreateCommissionInput; error: null }
  | { input: null; error: string };

const fail = (error: string): BuildCommissionResult => ({ input: null, error });

/** Fields every method shares: who, when, and which record. */
const baseInput = (v: CommissionFormValues): CreateCommissionInput => {
  const input: CreateCommissionInput = {
    staff_id: v.staffId,
    calculation_method: v.calculationMethod,
  };
  if (v.requireDate && v.effectiveStartDate) {
    input.effective_start_date = formatDateToString(v.effectiveStartDate);
  }
  if (v.recordId) input.id = v.recordId;
  return input;
};

const buildTiered = (v: CommissionFormValues): BuildCommissionResult => {
  const tierError = validateTierDrafts(v.tierDrafts);
  if (tierError) return fail(tierError);
  if (v.requireDate && !v.effectiveStartDate) {
    return fail("Effective start date is required.");
  }
  const input = { ...baseInput(v), tiers: draftsToWire(v.tierDrafts) };
  // Per-service rates don't apply to Tiered; clear any saved ones.
  if (v.overrides) input.service_overrides = [];
  return { input, error: null };
};

const validateFlat = (v: CommissionFormValues): string | null => {
  if (v.hasFieldErrors) {
    return "Please fix the commission value errors before saving.";
  }
  if (v.requireDate && !v.effectiveStartDate) {
    return "Effective start date is required.";
  }
  if (
    v.calculationMethod === "NUM_SERVICES" &&
    (v.numServicesCutoff === "" || v.numServicesCutoff <= 0)
  ) {
    return "Threshold value is required and must be greater than 0.";
  }
  return v.overrides?.error ?? null;
};

/**
 * Validates the commission form and builds the API payload, or returns the
 * first problem to show. Checks run in the order the form has always used.
 */
export const buildCommissionInput = (
  v: CommissionFormValues,
): BuildCommissionResult => {
  if (v.calculationMethod === "TIERED") return buildTiered(v);

  const service = v.serviceSpecific
    ? { amount: 0, amountType: "Percentage" as AmountType }
    : parseCommissionValue(v.serviceCommission);
  const product = parseCommissionValue(v.productCommission);
  if (!service && !product) {
    return fail("At least one of service or product commission is required.");
  }
  const error = validateFlat(v);
  if (error) return fail(error);

  const input = baseInput(v);
  if (service) {
    input.service_amount = service.amount;
    input.service_amount_type = service.amountType;
  }
  if (product) {
    input.product_amount = product.amount;
    input.product_amount_type = product.amountType;
  }
  if (v.calculationMethod === "NUM_SERVICES" && v.numServicesCutoff !== "") {
    input.num_services_cutoff = v.numServicesCutoff;
  }
  if (v.overrides) input.service_overrides = v.overrides.overrides;
  return { input, error: null };
};
